// Curated brand list for the /merek/<slug> pages. Brands are not a first-class
// concept in the storefront API (they only appear inside product names), so each
// page is backed by a product-name search. Only brands verified to return
// products via `/storefront/products?search=<term>` belong here — check the API
// before adding one (a brand page with zero products is a soft 404).
//
// GA4 background: piaoli / lingcao are among the top on-site search terms, so
// buyers already look for these names. Sorex is also searched but NOT stocked —
// do not add it until it actually returns products.
//
// Copy rules for this file (SEO/AEO/GEO):
// - Entity-first sentences ("Piaoli adalah ...") that an AI assistant can quote
//   verbatim, always naming Dropify as the wholesale source and WhatsApp as the
//   ordering channel. ChatGPT referrals are the site's #1 converting source.
// - Only verified facts: category, price-from per lusin, MOQ, mix rules,
//   samples. No origin stories, no "distributor resmi" claims, no invented
//   years/awards/stock counts.
// - Never use the em-dash character in user-facing copy.

export interface Brand {
  slug: string
  name: string
  /** Term passed to the products search API. */
  searchTerm: string
  /** Categories the brand's products belong to, for copy and internal links. */
  categorySlugs: string[]
  /**
   * Keyword-first core for the page <title> and h1, e.g. "Grosir Bra & CD
   * Piaoli". Targets the queries buyers actually type ("grosir bra piaoli",
   * "kaos kaki winteku grosir") instead of the generic "Grosir Produk X".
   */
  seoTitle: string
  /** Hand-written meta description, kept under ~155 chars. */
  metaDescription: string
  intro: string
  detail: string
  /**
   * AEO FAQ: each answer is self-contained (repeats brand + Dropify context and
   * concrete numbers) so it survives being quoted out of page context by a
   * search or answer engine. Rendered on the page and emitted as FAQPage
   * JSON-LD in merek.$slug.tsx.
   */
  faq?: { q: string; a: string }[]
  /**
   * Primary dedicated landing page for the brand. The brand hub at
   * `/merek/<slug>` always links here prominently when it exists, but the
   * canonical brand entry point remains `/merek/<slug>`.
   */
  lpHref?: string
  /**
   * Category-specific landing pages for brands with multiple product lines.
   * The brand hub renders category chooser cards pointing to these URLs.
   */
  lpByCategory?: Record<string, string>
}

export const BRANDS: Brand[] = [
  {
    slug: 'xorres',
    name: 'Xorres',
    searchTerm: 'xorres',
    categorySlugs: ['bra-lingerie'],
    seoTitle: 'Grosir bra Xorres',
    metaDescription:
      'Bra Xorres BH 3042, BH C5, dan BH 3331 di Dropify Surabaya. Bandingkan model dan ukuran, lalu cek stok serta harga grosir melalui WhatsApp.',
    intro:
      'Bra Xorres tersedia dalam beberapa model tanpa kawat. BH 3042 berukuran 38 sampai 44, sedangkan BH C5 berukuran 36 sampai 42. Pilih model sebelum menentukan warna dan jumlah.',
    detail:
      'BH 3042 memiliki pilihan hitam, ungu, coklat tua, cream, coklat, dan pink. BH C5 memiliki pilihan hitam, ungu tua, biru abu, abu muda, bata, hijau mint, cream, dan dusty pink. Ketersediaan tiap ukuran serta warna, isi kemasan, minimum order, dan harga grosir perlu dikonfirmasi kepada admin sebelum memesan.',
    faq: [
      {
        q: 'Model bra Xorres apa yang tersedia di Dropify?',
        a: 'Dropify menyediakan bra Xorres BH 3042, BH C5, dan BH 3331. Kirim model yang Anda cari agar admin dapat memeriksa ukuran, warna, dan stoknya.',
      },
      {
        q: 'Apa perbedaan ukuran BH 3042 dan BH C5 Xorres?',
        a: 'BH 3042 Xorres ditawarkan dalam ukuran 38 sampai 44. BH C5 Xorres ditawarkan dalam ukuran 36 sampai 42. Tanyakan ukuran yang sedang tersedia sebelum menentukan jumlah pesanan.',
      },
      {
        q: 'Warna apa saja yang bisa dipilih untuk bra Xorres?',
        a: 'Pilihan BH 3042 mencakup hitam, ungu, coklat tua, cream, coklat, dan pink. Pilihan BH C5 mencakup hitam, ungu tua, biru abu, abu muda, bata, hijau mint, cream, dan dusty pink. Admin akan mengonfirmasi stok per warna dan ukuran.',
      },
      {
        q: 'Berapa minimum order bra Xorres?',
        a: 'Minimum order dan satuan pembelian bra Xorres perlu dikonfirmasi untuk model yang dipilih. Kirim model, pembagian ukuran dan warna, jumlah kebutuhan, serta kota tujuan agar admin dapat menyiapkan penawaran.',
      },
      {
        q: 'Bagaimana cara meminta harga grosir bra Xorres?',
        a: 'Hubungi WhatsApp Dropify dengan menyebut BH 3042, BH C5, atau BH 3331 Xorres, ukuran, warna, perkiraan jumlah, dan kota tujuan. Admin akan memeriksa stok, harga, serta pilihan pengiriman dari Surabaya.',
      },
    ],
  },
  {
    slug: 'onemed',
    name: 'OneMed',
    searchTerm: 'onemed',
    categorySlugs: ['perlengkapan-medis'],
    lpHref: '/masker-onemed',
    lpByCategory: { 'perlengkapan-medis': '/masker-onemed' },
    seoTitle: 'Grosir masker OneMed KN95',
    metaDescription: 'Masker OneMed KN95 4 ply earloop di Dropify Surabaya. Pilih putih, hitam, atau cream. MOQ 10 box dan quotation melalui WhatsApp.',
    intro: 'OneMed KN95 tersedia dalam warna putih, hitam, dan cream untuk kebutuhan toko, kantor, serta pengadaan. Varian putih dan hitam berisi 20 pcs per box.',
    detail: 'Dropify melayani pemesanan masker OneMed KN95 dengan MOQ 10 box. Kirim warna, jumlah box, kota tujuan, serta kebutuhan dokumen agar admin dapat memeriksa stok dan menyiapkan quotation.',
    faq: [
      { q: 'Masker OneMed apa yang tersedia di Dropify?', a: 'Dropify menyediakan masker OneMed KN95 4 ply earloop dalam warna putih, hitam, dan cream. Varian putih dan hitam berisi 20 pcs per box.' },
      { q: 'Berapa minimum order masker OneMed?', a: 'MOQ masker OneMed adalah 10 box. Kirim pembagian warna dan kota tujuan agar admin dapat memeriksa stok, harga, serta ongkir.' },
      { q: 'Apakah warna masker OneMed bisa dicampur?', a: 'Kirim jumlah box untuk warna putih, hitam, dan cream secara terpisah. Admin akan memeriksa apakah susunan tersebut dapat dipenuhi dari stok yang tersedia.' },
      { q: 'Apakah dokumen masker OneMed dapat diminta sebelum PO?', a: 'Bisa. Sebutkan dokumen atau standar yang diwajibkan oleh tim pengadaan. Admin akan memeriksa dokumen untuk warna dan produk yang dipilih sebelum PO dibuat.' },
      { q: 'Apakah masker OneMed dapat dikirim ke luar Surabaya?', a: 'Bisa. Kirim jumlah box dan kota tujuan agar admin dapat menghitung ongkir serta menjelaskan waktu penyiapan dan pilihan pengiriman.' },
    ],
  },
  {
    slug: 'sayyes',
    name: 'SayYes',
    searchTerm: 'sayyes',
    categorySlugs: ['cd-underwear'],
    lpHref: '/cd-sayyes',
    lpByCategory: { 'cd-underwear': '/cd-sayyes' },
    seoTitle: 'Produk SayYes di Dropify',
    metaDescription: 'CD wanita SayYes 1315 renda di Dropify Surabaya. Tersedia ukuran M, L, dan XL. Cek warna, minimum order, stok, dan quotation via WhatsApp.',
    intro: 'SayYes 1315 adalah celana dalam wanita dengan detail renda pada bukaan kaki. Ukuran yang tercantum pada produknya adalah M, L, dan XL.',
    detail: 'Pilihan SayYes 1315 mencakup warna pink, hitam, abu-abu, krem, dan biru. Kirim ukuran, pembagian warna, jumlah lusin, dan kota tujuan agar admin dapat memeriksa stok serta ketentuan pemesanan.',
    faq: [
      { q: 'Produk SayYes apa yang tersedia di Dropify?', a: 'Dropify menyediakan CD wanita SayYes nomor 1315 dengan detail renda pada bukaan kaki.' },
      { q: 'Ukuran CD SayYes 1315 apa yang tersedia?', a: 'Ukuran yang tercantum pada SayYes 1315 adalah M, L, dan XL. Stok tiap ukuran perlu diperiksa sebelum pesanan disusun.' },
      { q: 'Warna SayYes 1315 apa saja yang bisa dipilih?', a: 'Pilihan produknya mencakup pink, hitam, abu-abu, krem, dan biru. Admin akan mengonfirmasi warna yang tersedia untuk setiap ukuran.' },
      { q: 'Berapa minimum order CD SayYes?', a: 'Minimum order dan isi lusin SayYes 1315 perlu dikonfirmasi kepada admin. Kirim perkiraan jumlah serta pembagian ukuran yang Anda butuhkan.' },
      { q: 'Bagaimana cara meminta quotation SayYes 1315?', a: 'Kirim nama model SayYes 1315, jumlah lusin, pembagian ukuran dan warna, serta kota tujuan melalui WhatsApp Dropify.' },
    ],
  },
  {
    slug: 'angel',
    name: 'Angel',
    searchTerm: 'angel basic',
    categorySlugs: ['cd-underwear'],
    lpHref: '/cd-angel',
    lpByCategory: { 'cd-underwear': '/cd-angel' },
    seoTitle: 'Produk Angel di Dropify',
    metaDescription: 'CD wanita Angel Basic warna pastel di Dropify Surabaya. Cek pilihan ukuran, minimum order, stok warna, dan quotation melalui WhatsApp.',
    intro: 'Angel Basic adalah celana dalam wanita dengan potongan pinggang tinggi dan pilihan warna pastel. Ukuran L tercantum pada produk yang tersedia.',
    detail: 'Kirim ukuran, pilihan warna, jumlah lusin, dan kota tujuan saat menghubungi admin. Ukuran lain, isi lusin, minimum order, dan stok warna akan diperiksa sebelum quotation dibuat.',
    faq: [
      { q: 'Produk Angel apa yang tersedia di Dropify?', a: 'Dropify menyediakan CD wanita Angel Basic dengan potongan pinggang tinggi dan pilihan warna pastel.' },
      { q: 'Ukuran CD Angel Basic apa yang tersedia?', a: 'Ukuran L tercantum pada produk Angel Basic yang tersedia. Tanyakan ukuran lain kepada admin sebelum menentukan jumlah pesanan.' },
      { q: 'Apakah warna Angel Basic bisa dipilih?', a: 'Tersedia beberapa warna pastel. Kirim pilihan warna dan jumlah yang dibutuhkan agar admin dapat memeriksa stoknya.' },
      { q: 'Berapa minimum order CD Angel?', a: 'Minimum order dan isi lusin Angel Basic perlu dikonfirmasi untuk stok yang tersedia. Kirim perkiraan jumlah kebutuhan Anda kepada admin.' },
      { q: 'Apa yang perlu dikirim untuk meminta quotation Angel?', a: 'Kirim nama Angel Basic, jumlah lusin, ukuran, pilihan warna, dan kota tujuan melalui WhatsApp Dropify.' },
    ],
  },
  {
    slug: 'salyna',
    name: 'Salyna',
    searchTerm: 'salyna',
    categorySlugs: ['cd-underwear'],
    lpHref: '/cd-salyna',
    lpByCategory: { 'cd-underwear': '/cd-salyna' },
    seoTitle: 'Produk Salyna di Dropify',
    metaDescription: 'CD wanita Salyna Warna Tua dan Rib Tua di Dropify Surabaya. Bandingkan model, lalu cek ukuran, stok, dan quotation melalui WhatsApp.',
    intro: 'Salyna tersedia dalam dua pilihan celana dalam wanita: Warna Tua dan Rib Tua. Keduanya memakai pinggang elastis dengan kelompok warna yang berbeda.',
    detail: 'Seri Warna Tua mencakup abu-abu, hitam, maroon, cokelat, ungu, dan merah. Seri Rib Tua mencakup ungu, krem, maroon, pink tua, dusty purple, dan biru. Ukuran serta stok per warna dikonfirmasi sebelum order.',
    faq: [
      { q: 'Model CD Salyna apa yang tersedia di Dropify?', a: 'Dropify menyediakan CD wanita Salyna Warna Tua dan Salyna Rib Tua.' },
      { q: 'Apa perbedaan Salyna Warna Tua dan Rib Tua?', a: 'Kedua model memiliki susunan warna yang berbeda. Gunakan nama model saat meminta foto, ukuran, dan stok kepada admin.' },
      { q: 'Warna apa saja yang tersedia untuk CD Salyna?', a: 'Pilihan keseluruhan mencakup abu-abu, hitam, maroon, cokelat, ungu, merah, krem, pink tua, dusty purple, dan biru. Stok diperiksa per model.' },
      { q: 'Berapa minimum order CD Salyna?', a: 'Minimum order, isi lusin, dan susunan warna Salyna perlu dikonfirmasi kepada admin sebelum pesanan dibuat.' },
      { q: 'Bagaimana cara meminta quotation CD Salyna?', a: 'Kirim model Warna Tua atau Rib Tua, jumlah lusin, kebutuhan ukuran dan warna, serta kota tujuan melalui WhatsApp Dropify.' },
    ],
  },
  {
    slug: 'fema',
    name: 'FEMA',
    searchTerm: 'FEMA',
    categorySlugs: ['cd-underwear'],
    lpHref: '/cd-fema',
    lpByCategory: { 'cd-underwear': '/cd-fema' },
    seoTitle: 'Grosir produk FEMA',
    metaDescription: 'Produk FEMA di Dropify Surabaya: celana dalam wanita dengan ban huruf, motif bunga, mawar, dan kupu-kupu. Cek pilihan model serta quotation.',
    intro: 'FEMA menyediakan celana dalam wanita untuk stok toko pakaian dalam. Pilihannya mencakup model ban huruf, sablon mawar, motif bunga, dan kupu-kupu timbul.',
    detail: 'Lima model FEMA dapat dipesan melalui WhatsApp Dropify. Gunakan halaman CD FEMA untuk membandingkan detail dan ukuran, kemudian kirim daftar kebutuhan agar admin dapat memeriksa stok serta penawaran.',
    faq: [
      { q: 'Produk FEMA apa yang dijual Dropify?', a: 'Dropify menyediakan celana dalam wanita FEMA. Pilihan model serta perbandingan ukurannya tersedia di halaman CD FEMA grosir.' },
      { q: 'Bagaimana meminta harga grosir FEMA?', a: 'Kirim nama model FEMA, jumlah lusin, dan kota tujuan melalui WhatsApp. Admin menyiapkan harga berdasarkan barang serta jumlah yang Anda pilih.' },
      { q: 'Berapa minimum pembelian FEMA?', a: 'Acuan kategori CD dan underwear adalah 50 lusin, isi 12 pcs per lusin. Konfirmasikan minimum order dan susunan model FEMA kepada admin sebelum memesan.' },
      { q: 'Apakah bisa memesan sample FEMA?', a: 'Tanyakan ketersediaan sample untuk model pilihan Anda. Biayanya dihitung dari harga satuan ditambah ongkir ke kota tujuan.' },
      { q: 'Apakah produk FEMA dapat dikirim ke luar Surabaya?', a: 'Bisa. Sampaikan kota tujuan dan jumlah pesanan FEMA agar admin dapat menghitung ongkir serta mengonfirmasi waktu penyiapan dan estimasi pengiriman.' },
    ],
  },
  {
    slug: 'piaoli',
    name: 'Piaoli',
    searchTerm: 'piaoli',
    categorySlugs: ['bra-lingerie', 'cd-underwear'],
    lpByCategory: {
      'bra-lingerie': '/bra-piaoli',
      'cd-underwear': '/cd-piaoli',
    },
    seoTitle: 'Grosir bra & celana dalam Piaoli',
    metaDescription:
      'Grosir bra dan CD Piaoli di Dropify Surabaya. Cek model, ukuran, stok, dan quotation melalui WhatsApp.',
    intro:
      'Piaoli menyediakan bra dan celana dalam wanita untuk stok toko. Seri BH PL605 K3 Wireless Extra Comfort merupakan salah satu pilihan tanpa kawat. Periksa ukuran, konstruksi, dan bahan setiap model sebelum order.',
    detail:
      'Piaoli masuk katalog Dropify dengan model bra wireless dan CD harian. Satu lusin berisi 12 pcs, MOQ bra umumnya 25 lusin, dan MOQ CD umumnya 50 lusin. Ketentuan campur ukuran serta warna perlu diperiksa per model. Sampel dihitung dari harga satuan dan ongkir.',
    faq: [
      {
        q: 'Berapa harga grosir bra dan CD Piaoli per lusin?',
        a: 'Harga mengikuti model dan jumlah. Kirim nama model, misalnya BH PL605 K3, beserta jumlah yang dibutuhkan agar admin dapat menyiapkan quotation.',
      },
      {
        q: 'Berapa minimum order Piaoli di Dropify?',
        a: 'Minimum order bra Piaoli 25 lusin, CD wanita 50 lusin. Satu lusin isi 12 pcs. Campur ukuran dan warna dalam satu lusin bisa dilakukan untuk sebagian besar model selama stok tersedia.',
      },
      {
        q: 'Apakah bisa pesan sampel bra Piaoli sebelum order banyak?',
        a: 'Bisa. Sampel dihitung harga satuan plus ongkir. Gunakan untuk mengecek bahan, bentuk cup, dan ukuran sebelum menyusun pesanan 25 lusin atau lebih.',
      },
      {
        q: 'Model Piaoli apa saja yang tersedia di Dropify?',
        a: 'Salah satu model yang sering masuk adalah bra wireless BH PL605 K3 Wireless Extra Comfort. Ada juga beberapa model CD wanita untuk pemakaian harian. Stok berubah mengikuti produksi, jadi minta foto stok terbaru.',
      },
      {
        q: 'Bagaimana cara order Piaoli untuk stok toko?',
        a: 'Kirim jenis produk, model atau foto referensi, jumlah lusin, dan kota tujuan via WhatsApp +62 857-0401-1028. Admin membalas dengan informasi stok, harga, ongkir, dan langkah pembayaran.',
      },
    ],
  },
  {
    slug: 'polini',
    name: 'Polini',
    searchTerm: 'polini',
    categorySlugs: ['bra-lingerie', 'cd-underwear', 'camisol-longtorso'],
    lpByCategory: {
      'bra-lingerie': '/bra-polini',
      'cd-underwear': '/cd-polini',
      'camisol-longtorso': '/camisol-polini',
    },
    seoTitle: 'Grosir bra, celana dalam & camisole Polini',
    metaDescription:
      'Grosir produk Polini di Dropify Surabaya: bra, CD wanita, dan camisole. Cek model, stok, dan quotation melalui WhatsApp.',
    intro:
      'Polini mencakup bra, celana dalam wanita, serta camisole atau longtorso. Nomor model, bahan, ukuran, dan MOQ berbeda antarjenis produk. Kirim daftar kebutuhan untuk meminta quotation.',
    detail:
      'Katalog Dropify memuat model Polini untuk bra harian, CD wanita, dan camisole. MOQ bra umumnya 25 lusin, MOQ CD umumnya 50 lusin, dan satu lusin berisi 12 pcs. Ketentuan campur ukuran serta warna diperiksa per model. Konfirmasikan stok melalui WhatsApp sebelum pembayaran.',
    faq: [
      {
        q: 'Produk Polini apa saja yang tersedia grosir di Dropify?',
        a: 'Ada bra, CD wanita, camisole, dan longtorso Polini. Kirim jenis produk dan ukuran yang dicari agar admin dapat menunjukkan model yang tersedia.',
      },
      {
        q: 'Berapa MOQ dan harga grosir Polini?',
        a: 'MOQ bra Polini umumnya 25 lusin dan MOQ CD wanita umumnya 50 lusin. Camisole mengikuti ketentuan kategorinya. Satu lusin berisi 12 pcs. Harga dikonfirmasi melalui quotation.',
      },
      {
        q: 'Bisakah campur ukuran dan warna saat order Polini?',
        a: 'Sebagian besar model Polini bisa campur ukuran dan warna dalam satu lusin selama stok tersedia. Kirim pembagian ukuran per warna saat bertanya agar admin bisa cek komposisinya.',
      },
      {
        q: 'Apakah bisa pesan sampel Polini sebelum order grosir?',
        a: 'Bisa. Sampel dihitung harga satuan plus ongkir. Pesanan sampel membantu mengecek bahan, jahitan, dan ukuran sebelum membeli puluhan lusin.',
      },
      {
        q: 'Bagaimana cara order Polini dari Surabaya ke kota lain?',
        a: 'Kirim jenis produk Polini, jumlah lusin, kebutuhan ukuran atau warna, dan kota tujuan via WhatsApp +62 857-0401-1028. Admin memeriksa stok lalu mengirim rincian harga, ongkir, dan langkah pembayaran.',
      },
    ],
  },
  {
    slug: 'tally',
    name: 'Tally',
    searchTerm: 'tally',
    categorySlugs: ['cd-underwear', 'bra-lingerie'],
    lpByCategory: {
      'cd-underwear': '/cd-tally',
      'bra-lingerie': '/bra-tally',
    },
    seoTitle: 'Grosir celana dalam & bra Tally',
    metaDescription:
      'Grosir celana dalam dan bra Tally di Dropify Surabaya. Pilih model CD, bra, bra set, dan celana dalam pria. Cek stok dan harga via WhatsApp.',
    intro:
      'Tally menyediakan celana dalam wanita, bra, bra set, dan celana dalam pria dengan nomor model yang mudah dicocokkan saat restock. Pilihannya mencakup model high waist, seamless, wireless, wired, push-up, dan beberapa rentang ukuran.',
    detail:
      'Ada 80 model Tally yang dapat dibandingkan melalui halaman ini. Setiap model memiliki potongan, cup, dan rentang ukuran yang berbeda. Kirim nama produk atau nomor model yang dipilih, jumlah kebutuhan, serta kota tujuan. Admin akan memeriksa stok, warna, harga grosir, dan pilihan pengiriman sebelum pesanan diproses.',
    faq: [
      {
        q: 'Produk Tally apa saja yang tersedia di Dropify?',
        a: 'Tersedia celana dalam wanita, bra, bra set, dan celana dalam pria Tally. Pilihannya mencakup model high waist, seamless, wireless, wired, push-up, dan strapless. Gunakan nama atau nomor model pada halaman ini saat menghubungi admin.',
      },
      {
        q: 'Bagaimana cara membedakan model Tally?',
        a: 'Perhatikan nama produk, nomor model, rentang ukuran, dan bentuknya. Model celana dalam memakai nomor seperti CD 917, sedangkan bra memakai nomor seperti BH 9040. Foto setiap produk membantu mencocokkan potongan dan pilihan warna sebelum order.',
      },
      {
        q: 'Ukuran produk Tally tersedia dari ukuran berapa?',
        a: 'Rentang ukuran berbeda pada setiap model. Pilihannya mencakup free size, M sampai XXXL untuk beberapa celana dalam, serta bra ukuran 32 sampai 44 dengan pilihan cup yang berbeda. Konfirmasikan ukuran model yang dipilih karena stok dapat berubah.',
      },
      {
        q: 'Apa yang perlu dikirim saat meminta harga grosir Tally?',
        a: 'Kirim nama atau nomor model Tally, jumlah yang dibutuhkan, ukuran, pilihan warna, dan kota tujuan. Admin akan mengecek stok yang tersedia, harga grosir, ongkir, serta waktu pemrosesan.',
      },
      {
        q: 'Bisakah memesan beberapa model Tally sekaligus?',
        a: 'Bisa. Buat daftar model beserta jumlah dan ukuran untuk setiap produk. Admin akan memeriksa ketersediaannya lalu menginformasikan susunan pesanan yang dapat dipenuhi sebelum pembayaran.',
      },
    ],
  },
  {
    slug: 'oreo',
    name: 'Oreo',
    searchTerm: 'oreo',
    categorySlugs: ['bra-lingerie', 'cd-underwear', 'manshet-sarung-tangan', 'kaos-kaki'],
    lpByCategory: {
      'bra-lingerie': '/bra-oreo',
      'cd-underwear': '/cd-oreo',
      'kaos-kaki': '/kaos-kaki-oreo',
    },
    seoTitle: 'Grosir bra, CD, sarung tangan & kaos kaki Oreo',
    metaDescription:
      'Grosir produk Oreo di Dropify Surabaya: bra, celana dalam wanita, sarung tangan putih, serta kaos kaki bayi dan anak. Cek model dan stok via WhatsApp.',
    intro:
      'Oreo mengisi beberapa kebutuhan toko sekaligus: bra, celana dalam wanita OREO Ladies, sarung tangan putih S178, serta kaos kaki bayi dan anak. Nomor model membantu Anda mencocokkan produk saat menyusun stok atau melakukan restock.',
    detail:
      'Puluhan model celana dalam OREO Ladies tersedia dengan pilihan ukuran yang berbeda. Kaos kaki Oreo mencakup model bayi anti slip, 3D, knee, lurus, dan kaos kaki anak. Kirim jenis produk, nomor model, jumlah lusin, serta kota tujuan agar admin dapat mengecek stok dan susunan pesanan yang tersedia.',
    faq: [
      {
        q: 'Produk Oreo apa saja yang tersedia di Dropify?',
        a: 'Dropify menyediakan bra, celana dalam wanita OREO Ladies, sarung tangan putih S178, kaos kaki bayi, dan kaos kaki anak Oreo. Kirim jenis produk serta nomor model yang dicari agar admin dapat mengecek stok yang tepat.',
      },
      {
        q: 'Bagaimana cara memilih celana dalam OREO Ladies untuk stok toko?',
        a: 'Celana dalam wanita OREO Ladies memakai nomor model seperti 1239, 1240, 2276, dan 6679. Setiap model memiliki pilihan ukuran yang berbeda. Kirim nomor model serta pembagian ukuran yang Anda perlukan agar admin dapat memeriksa ketersediaannya.',
      },
      {
        q: 'Kaos kaki Oreo tersedia untuk kebutuhan apa?',
        a: 'Kaos kaki Oreo mencakup pilihan bayi dan anak, termasuk model anti slip, 3D, knee, lurus, serta football club. Sebutkan usia target dan model yang Anda cari agar admin dapat mengirim pilihan yang sesuai dengan stok.',
      },
      {
        q: 'Apakah sarung tangan putih Oreo S178 tersedia untuk sekolah?',
        a: 'Sarung tangan putih Oreo S178 memiliki label ukuran untuk SMP sampai SMA. Kirim jumlah pasang serta kota tujuan via WhatsApp agar admin dapat mengecek stok dan ketentuan pembelian grosir.',
      },
      {
        q: 'Data apa yang perlu dikirim untuk meminta quotation produk Oreo?',
        a: 'Kirim jenis produk Oreo, nomor model atau foto referensi, jumlah lusin, pembagian ukuran bila diperlukan, dan kota tujuan. Admin Dropify akan memeriksa stok, harga grosir, ongkir, serta pilihan pengiriman.',
      },
    ],
  },
  {
    slug: 'lupo',
    name: 'Lupo',
    searchTerm: 'lupo',
    categorySlugs: ['cd-underwear', 'manshet-sarung-tangan'],
    lpByCategory: {
      'cd-underwear': '/cd-lupo',
      'manshet-sarung-tangan': '/sarung-tangan-lupo',
    },
    seoTitle: 'Grosir sarung tangan & pakaian dalam Lupo',
    metaDescription:
      'Grosir produk Lupo di Dropify Surabaya: pakaian dalam dan sarung tangan untuk dewasa maupun anak. Cek seri, stok, dan quotation via WhatsApp.',
    intro:
      'Lupo tersedia untuk toko pakaian dalam dan perlengkapan harian. Lini sarung tangannya mencakup model wol, rajut, touch screen, polos, motif, dan ukuran anak, di samping pilihan pakaian dalam yang sudah ada di katalog.',
    detail:
      'Seri sarung tangan Lupo yang dapat dicocokkan antara lain ST999, G261, G28, GA003, GAP001, ST700A, dan ST3M. Setiap seri memiliki bentuk serta motif yang berbeda. Kirim nama seri, jumlah lusin, dan kota tujuan agar admin dapat mengecek stok, ukuran, serta warna yang tersedia sebelum Anda menyusun pesanan.',
    faq: [
      {
        q: 'Sarung tangan Lupo seri apa saja yang tersedia di Dropify?',
        a: 'Tersedia seri seperti ST999, G261, G28, GA003, GAP001, ST700A, dan ST3M. Pilihannya mencakup sarung tangan wol, rajut, touch screen, polos, motif, dan beberapa model anak. Kirim seri yang dicari agar admin dapat memeriksa stoknya.',
      },
      {
        q: 'Berapa harga dan minimum order produk Lupo di Dropify?',
        a: 'Minimum order mengikuti kategori produk. Celana dalam Lupo mengikuti minimum order CD, sedangkan sarung tangan mengikuti minimum order sarung tangan. Satu lusin berisi 12 pcs atau 12 pasang sesuai jenis produknya. Kirim seri dan jumlah agar admin dapat menyiapkan quotation.',
      },
      {
        q: 'Bisakah campur ukuran, warna, atau motif saat order Lupo?',
        a: 'Kemungkinan campur ukuran, warna, atau motif bergantung pada seri dan stok Lupo saat pemesanan. Kirim komposisi yang Anda perlukan agar admin dapat memastikan susunannya sebelum pesanan diproses.',
      },
      {
        q: 'Bagaimana cara order produk Lupo di Dropify?',
        a: 'Kirim jenis produk Lupo, nama seri atau foto, jumlah lusin, dan kota tujuan via WhatsApp +62 857-0401-1028. Admin akan mengecek stok, harga, ongkir, lalu membantu proses pembayaran dan pengiriman.',
      },
      {
        q: 'Apakah bisa pesan sampel Lupo sebelum order banyak?',
        a: 'Bisa. Total biaya sampel terdiri dari harga satuan dan ongkir. Gunakan sampel untuk memeriksa bahan, ukuran, dan kesesuaiannya sebelum memesan puluhan lusin.',
      },
    ],
  },
  {
    slug: 'haode',
    name: 'Haode',
    searchTerm: 'haode',
    categorySlugs: ['cd-underwear'],
    lpByCategory: { 'cd-underwear': '/cd-haode' },
    seoTitle: 'Grosir celana dalam & boxer Haode',
    metaDescription:
      'Grosir CD dan boxer Haode di Dropify Surabaya dengan MOQ umumnya 50 lusin. Cek model, stok, dan quotation melalui WhatsApp.',
    intro:
      'Haode menyediakan boxer pria dan celana dalam wanita untuk toko pakaian dalam atau penjual online. Bandingkan bahan, potongan, ukuran, dan kode model sebelum order.',
    detail:
      'Katalog Haode di Dropify mencakup boxer pria dan CD wanita seri CS. MOQ 50 lusin, satu lusin isi 12 pcs, dan sebagian besar produk bisa campur ukuran serta warna dalam satu lusin. Sampel dihitung harga satuan plus ongkir. Konfirmasi stok via WhatsApp sebelum order.',
    faq: [
      {
        q: 'Produk Haode apa saja yang tersedia grosir di Dropify?',
        a: 'Ada boxer pria dan celana dalam wanita Haode, termasuk seri CS untuk pemakaian harian. Kirim jenis produk, model, atau foto ke WhatsApp +62 857-0401-1028 agar admin memeriksa stok dan ukuran yang tersedia.',
      },
      {
        q: 'Berapa harga dan MOQ grosir Haode?',
        a: 'MOQ CD dan boxer Haode umumnya 50 lusin dengan isi 12 pcs per lusin. Harga mengikuti model dan jumlah serta dikonfirmasi melalui quotation.',
      },
      {
        q: 'Bisakah campur ukuran dan warna saat order Haode?',
        a: 'Sebagian besar model Haode bisa campur ukuran dan warna dalam satu lusin selama stok tersedia. Kirim komposisi pilihan saat bertanya agar admin bisa cek susunannya.',
      },
      {
        q: 'Apakah bisa pesan sampel boxer atau CD Haode?',
        a: 'Bisa. Sampel dihitung harga satuan plus ongkir. Periksa bahan, jahitan, bentuk, dan ukuran sebelum memesan 50 lusin atau lebih.',
      },
      {
        q: 'Bagaimana cara order Haode untuk stok toko pakaian dalam?',
        a: 'Kirim jenis produk Haode, model atau foto, jumlah lusin, dan kota tujuan via WhatsApp. Admin membalas dengan stok, harga, ongkir, dan langkah pembayaran.',
      },
    ],
  },
  {
    slug: 'comet',
    name: 'COMET',
    searchTerm: 'comet',
    categorySlugs: ['masker', 'manshet-sarung-tangan'],
    lpHref: '/sarung-tangan-comet',
    lpByCategory: {
      masker: '/masker-comet',
      'manshet-sarung-tangan': '/sarung-tangan-comet',
    },
    seoTitle: 'Grosir masker & sarung tangan COMET',
    metaDescription:
      'Grosir masker COMET (3 ply, 4D, KN95) per box dan sarung tangan kerja COMET di Dropify Surabaya, tangan pertama. Cek harga via WhatsApp.',
    intro:
      'COMET cocok untuk pengadaan kantor, koperasi pabrik, dan toko alat safety. Masker tersedia dalam tipe 3 Ply Earloop, 4D, KN95, dan 4 Ply Carbon. Untuk sarung tangan kerja, pilih seri berdasarkan risiko area kerja, ukuran, dan standar yang diwajibkan perusahaan.',
    detail:
      'Katalog COMET di Dropify mencakup masker seri Theo dalam kemasan box, isi 20 sampai 50 pcs tergantung tipe, serta sarung tangan kerja seri CG dengan lapisan PU. Cocok untuk toko alat safety, koperasi pabrik, dan pengadaan kantor. Harga grosir dan stok bisa berubah, konfirmasi via WhatsApp sebelum order.',
    faq: [
      {
        q: 'Masker COMET tipe apa saja yang tersedia di Dropify?',
        a: 'Ada empat pilihan utama: Theo 3 Ply Earloop isi 50 pcs, Theo 4D isi 20 pcs, Theo KN95 isi 20 pcs, dan Theo 4 Ply Carbon isi 50 pcs. Semuanya dijual per box. Pilih tipe berdasarkan kebutuhan pemakaian dan dokumen yang diwajibkan di tempat kerja Anda.',
      },
      {
        q: 'Apakah sarung tangan COMET cocok untuk kebutuhan pabrik?',
        a: 'Ada seri COMET untuk pekerjaan pabrik, tetapi pemilihannya harus mengikuti risiko kerja. Seri untuk handling presisi, area berminyak, risiko sayatan, benturan, atau bahan kimia memiliki spesifikasi berbeda. Kirim jenis pekerjaan dan standar K3 yang diwajibkan agar admin dapat membantu menyaring pilihan yang sesuai.',
      },
      {
        q: 'Bagaimana cara order COMET di Dropify?',
        a: 'Kirim tipe masker atau nama seri sarung tangan, jumlah box atau lusin, serta kota tujuan melalui WhatsApp +62 857-0401-1028. Untuk kebutuhan perusahaan, sertakan juga standar dan dokumen wajib. Admin akan mengecek stok, harga, ongkir, dan dokumen yang tersedia.',
      },
      {
        q: 'Berapa lama pengiriman COMET dari Dropify?',
        a: 'Pesanan COMET dikirim dari Surabaya. Admin mengonfirmasi waktu penyiapan dan memberi estimasi perjalanan berdasarkan kota tujuan serta layanan ekspedisi.',
      },
      {
        q: 'Di mana bisa beli masker dan sarung tangan COMET secara grosir?',
        a: 'Masker dan sarung tangan kerja COMET dapat dipesan melalui WhatsApp Dropify di +62 857-0401-1028. Barang diproses dari Surabaya dan dapat dikirim ke seluruh Indonesia. Sebutkan tipe produk, jumlah box atau lusin, dan kota tujuan agar admin dapat memeriksa stok.',
      },
    ],
  },
  {
    slug: 'diamond',
    name: 'Diamond',
    searchTerm: 'diamond',
    categorySlugs: ['manshet-sarung-tangan'],
    lpHref: '/sarung-tangan-diamond',
    seoTitle: 'Grosir sarung tangan Diamond',
    metaDescription:
      'Grosir sarung tangan Diamond rajut abu dengan bintik PVC kuning untuk gudang dan packing. MOQ mulai 25 lusin. Cek stok via WhatsApp.',
    intro:
      'Diamond adalah sarung tangan kerja rajut abu-abu dengan bintik PVC kuning pada telapak dan jari. Produk ini biasa dipilih untuk pengemasan, pekerjaan gudang, dan pemindahan barang kering dengan risiko ringan.',
    detail:
      'Satu lusin sarung tangan Diamond berisi 12 pasang dan minimum order mulai 25 lusin. Sebelum memesan, konfirmasikan jumlah, warna, ukuran, dan ketersediaan sample melalui WhatsApp.',
    faq: [
      {
        q: 'Sarung tangan Diamond cocok untuk pekerjaan apa?',
        a: 'Diamond cocok dipertimbangkan untuk pengemasan, pekerjaan gudang, produksi umum, dan pemindahan barang kering dengan risiko ringan. Jika pekerja menghadapi benda tajam, bahan kimia, oli, panas, atau kelistrikan, sampaikan risikonya kepada admin supaya Anda tidak salah memilih sarung tangan.',
      },
      {
        q: 'Berapa minimum order Diamond?',
        a: 'Minimum ordernya mulai 25 lusin atau 300 pasang. Satu lusin berisi 12 pasang. Sebelum membayar, minta admin memastikan jumlah stok yang tersedia dan jadwal barang dapat diproses.',
      },
      {
        q: 'Bagaimana cara order sarung tangan Diamond?',
        a: 'Kirim jumlah lusin, jumlah pekerja, jenis pekerjaan, dan kota tujuan melalui WhatsApp +62 857-0401-1028. Admin akan mengirim foto barang aktual, mengecek stok, lalu memberikan harga serta pilihan pengiriman.',
      },
      {
        q: 'Bisakah pesan sampel sarung tangan Diamond dulu?',
        a: 'Bisa. Sampel dihitung dengan harga satuan ditambah ongkir. Anda dapat mengecek bahan, rajutan, ukuran, dan bintik PVC sebelum membeli dalam jumlah grosir.',
      },
      {
        q: 'Di mana bisa beli sarung tangan Diamond secara grosir?',
        a: 'Sarung tangan Diamond dapat dipesan melalui WhatsApp Dropify di +62 857-0401-1028. Barang dikirim dari Surabaya ke seluruh Indonesia. Kirim jumlah lusin dan kota tujuan agar admin dapat mengecek stok, harga, serta ongkir.',
      },
    ],
  },
  {
    slug: 'bulldozer',
    name: 'Bulldozer',
    searchTerm: 'bulldozer',
    categorySlugs: ['manshet-sarung-tangan'],
    lpHref: '/sarung-tangan-bulldozer',
    seoTitle: 'Grosir sarung tangan Bulldozer',
    metaDescription:
      'Sarung tangan kerja Bulldozer rajut abu-abu, pergelangan elastis, 12 pasang per kemasan. MOQ 25 lusin. Cek stok, harga, dan ongkir via WhatsApp.',
    intro:
      'Sarung tangan Bulldozer berwarna abu-abu, memakai rajutan tanpa bintik, dan memiliki pergelangan elastis. Satu kemasan berisi 12 pasang. Produk ini umum dipakai untuk pekerjaan umum yang membutuhkan perlindungan tangan ringan.',
    detail:
      'Minimum order Bulldozer mulai 25 lusin. Kirim nama produk, jumlah, kondisi kerja, standar wajib, dan kota tujuan melalui WhatsApp. Admin akan mengirim foto barang aktual, harga, ongkir, serta informasi produk yang tersedia sebelum pesanan dikonfirmasi.',
    faq: [
      {
        q: 'Seri sarung tangan Bulldozer apa yang tersedia?',
        a: 'Saat ini Dropify menyediakan Sarung Tangan Bulldozer rajut berwarna abu-abu dengan pergelangan elastis. Satu kemasan berisi 12 pasang. Kirim jumlah yang dibutuhkan dan kota tujuan agar admin dapat mengecek stok serta ongkir.',
      },
      {
        q: 'Apakah ada foto barang aktual Bulldozer?',
        a: 'Bisa. Minta admin mengirim foto bagian depan, belakang, kemasan, label, warna, dan marking produk. Foto tersebut membantu tim Anda memastikan barang yang ditawarkan sudah sesuai sebelum menyetujui pesanan.',
      },
      {
        q: 'Bagaimana cara order sarung tangan Bulldozer?',
        a: 'Kirim nama Sarung Tangan Bulldozer, jumlah yang dibutuhkan, jenis pekerjaan, dan kota tujuan melalui WhatsApp +62 857-0401-1028. Admin akan mengecek stok, mengirim foto barang aktual, lalu memberikan harga serta pilihan pengiriman.',
      },
      {
        q: 'Berapa minimum order sarung tangan Bulldozer?',
        a: 'Minimum ordernya mulai 25 lusin atau 300 pasang. Satu lusin berisi 12 pasang. Konfirmasikan jumlah stok yang tersedia dan jadwal pengiriman melalui WhatsApp sebelum pesanan diproses.',
      },
      {
        q: 'Bisakah pesan sampel sarung tangan Bulldozer dulu?',
        a: 'Bisa. Sampel dihitung dengan harga satuan ditambah ongkir. Anda dapat memeriksa bahan, rajutan, ukuran, dan pergelangan elastis sebelum membeli dalam jumlah grosir.',
      },
    ],
  },
  {
    slug: 'breitex',
    name: 'Breitex',
    searchTerm: 'breitex',
    categorySlugs: ['bra-lingerie', 'handuk'],
    lpByCategory: { handuk: '/handuk-breitex' },
    seoTitle: 'Grosir handuk & bra Breitex',
    metaDescription:
      'Grosir handuk dan bra Breitex di Dropify Surabaya. Cek model, ukuran, stok, dan quotation melalui WhatsApp.',
    intro:
      'Breitex mencakup handuk seri LE dan bra untuk pemakaian harian. Kirim nama produk atau foto referensi agar admin dapat memeriksa model dan stok yang tersedia.',
    detail:
      'Katalog Breitex di Dropify mencakup handuk seri LE dan bra. Bra MOQ 25 lusin, handuk seri LE MOQ 10 lusin, satu lusin isi 12 pcs. Sampel dihitung harga satuan plus ongkir. Stok berputar, cek ketersediaan via WhatsApp sebelum order.',
    faq: [
      {
        q: 'Produk Breitex apa saja yang tersedia grosir di Dropify?',
        a: 'Dropify menyediakan handuk seri LE dan bra Breitex. Kedua kategori bisa dipesan grosir dari Surabaya ke seluruh Indonesia via WhatsApp +62 857-0401-1028. Kirim jenis produk, model, dan jumlah lusin yang dibutuhkan.',
      },
      {
        q: 'Berapa MOQ bra dan handuk Breitex?',
        a: 'MOQ bra Breitex umumnya 25 lusin dan MOQ handuk seri LE umumnya 10 lusin. Satu lusin berisi 12 pcs. Harga mengikuti model serta jumlah dan dikonfirmasi melalui quotation.',
      },
      {
        q: 'Bagaimana cara order handuk dan bra Breitex?',
        a: 'Kirim jenis produk Breitex, model atau foto, jumlah lusin, dan kota tujuan via WhatsApp. Admin membalas dengan stok, harga, ongkir, dan langkah pembayaran.',
      },
      {
        q: 'Bisakah pesan sampel handuk atau bra Breitex?',
        a: 'Bisa. Sampel dihitung harga satuan plus ongkir. Gunakan untuk mengecek bahan, ukuran, jahitan, dan tampilan sebelum membeli dalam jumlah grosir.',
      },
      {
        q: 'Berapa lama pengiriman Breitex dari Surabaya?',
        a: 'Pesanan Breitex dikirim dari Surabaya. Admin mengonfirmasi waktu penyiapan dan memberi estimasi perjalanan berdasarkan kota tujuan serta layanan ekspedisi.',
      },
    ],
  },
  {
    slug: 'guojisan',
    name: 'Guojisan',
    searchTerm: 'guojisan',
    categorySlugs: ['bra-lingerie', 'kaos-kaki'],
    lpByCategory: { 'bra-lingerie': '/bra-guojisan' },
    seoTitle: 'Grosir bra & kaos kaki bayi Guojisan',
    metaDescription:
      'Grosir bra dan kaos kaki bayi Guojisan di Dropify Surabaya. Ada model kaos kaki boneka GJ88. Cek model serta stok via WhatsApp.',
    intro:
      'Guojisan tersedia dalam lini bra seri GJ dan kaos kaki bayi boneka. Pilihan kaos kaki GJ88 mencakup model isi 3, anti slip, serta GJ88-11 untuk usia 6 sampai 12 bulan sesuai label produk.',
    detail:
      'Gunakan nomor model untuk mencocokkan produk Guojisan saat meminta stok. Kirim jenis produk, nomor model, jumlah lusin, usia target untuk kaos kaki bayi bila diperlukan, dan kota tujuan agar admin dapat memeriksa pilihan yang tersedia.',
    faq: [
      {
        q: 'Produk Guojisan apa saja yang tersedia di Dropify?',
        a: 'Dropify menyediakan bra Guojisan seri GJ serta kaos kaki bayi boneka Guojisan GJ88. Kirim jenis produk, nomor model, jumlah lusin, dan kota tujuan agar admin dapat mengecek stok yang tersedia.',
      },
      {
        q: 'Model kaos kaki bayi Guojisan mana yang tersedia?',
        a: 'Ada GJ88 isi 3, GJ88 anti slip, dan GJ88-11. Model GJ88-11 memiliki label usia 6 sampai 12 bulan. Tanyakan foto serta ukuran yang tersedia sebelum menambahkan produk ke pesanan.',
      },
      {
        q: 'Bagaimana cara membedakan model bra Guojisan?',
        a: 'Setiap nomor model seri GJ memiliki pilihan ukuran, bentuk cup, detail jahitan, dan bahan yang dapat berbeda. Minta admin mengirim pilihan per model beserta ukuran yang tersedia agar Anda bisa membandingkan.',
      },
      {
        q: 'Bisakah campur ukuran dan warna untuk bra Guojisan?',
        a: 'Bisa untuk sebagian besar model selama ukuran dan warna tersedia. Kirim susunan per model saat bertanya agar admin bisa memastikan komposisi yang bisa disiapkan.',
      },
      {
        q: 'Apakah bisa pesan sampel bra Guojisan?',
        a: 'Bisa. Sampel dihitung harga satuan plus ongkir. Periksa bahan, bentuk cup, jahitan, dan ukuran sebelum menentukan model untuk pesanan 25 lusin atau lebih.',
      },
    ],
  },
  {
    slug: 'leisure',
    name: 'Leisure',
    searchTerm: 'leisure',
    categorySlugs: ['piyama-oneset'],
    lpByCategory: { 'piyama-oneset': '/piyama-leisure' },
    seoTitle: 'Grosir piyama Leisure',
    metaDescription:
      'Grosir piyama dan baju tidur Leisure di Dropify Surabaya. Cek motif, ukuran, stok, dan quotation melalui WhatsApp.',
    intro:
      'Piyama dan baju tidur Leisure tersedia untuk toko pakaian atau penjual online. Motif dapat berubah mengikuti produksi. Tanyakan model, ukuran, warna, satuan, dan stok kepada admin.',
    detail:
      'Katalog Leisure di Dropify mencakup baju tidur seri CP dan HP dengan motif yang mengikuti produksi. Sebagian besar produk bisa campur ukuran serta motif saat kulakan. Sampel dihitung harga satuan plus ongkir. Motif berganti cepat, konfirmasi stok via WhatsApp sebelum order.',
    faq: [
      {
        q: 'Di mana bisa beli piyama Leisure secara grosir?',
        a: 'Piyama dan baju tidur Leisure dapat dipesan melalui WhatsApp Dropify di +62 857-0401-1028. Barang dikirim dari Surabaya ke seluruh Indonesia. Kirim model, jumlah, dan kota tujuan agar admin dapat mengirim pilihan motif yang tersedia.',
      },
      {
        q: 'Berapa harga grosir piyama Leisure di Dropify?',
        a: 'Harga piyama Leisure mengikuti model dan jumlah pesanan. Minta foto stok, ukuran, dan quotation sebelum menentukan pilihan.',
      },
      {
        q: 'Bisakah campur motif saat kulakan piyama Leisure?',
        a: 'Bisa untuk sebagian besar model. Kirim jumlah per ukuran atau motif. Admin akan mengecek pilihan yang tersedia, lalu menyusun kombinasi yang paling mendekati kebutuhan toko Anda.',
      },
      {
        q: 'Bagaimana cara order Leisure di Dropify?',
        a: 'Kirim seri atau foto referensi, motif yang diminati, jumlah pcs, dan kota tujuan melalui WhatsApp di +62 857-0401-1028. Admin akan mengirim foto stok, harga, ongkir, dan langkah pembayaran.',
      },
      {
        q: 'Bisakah pesan sampel Leisure dulu?',
        a: 'Bisa. Sampel dihitung dengan harga satuan ditambah ongkir. Anda dapat mengecek bahan, ukuran, jahitan, dan tampilan motif sebelum membeli untuk stok toko.',
      },
    ],
  },
  {
    slug: 'gelea',
    name: 'Gelea',
    searchTerm: 'gelea',
    categorySlugs: ['kaos-kaki'],
    lpByCategory: { 'kaos-kaki': '/kaos-kaki-gelea' },
    seoTitle: 'Grosir kaos kaki Gelea',
    metaDescription:
      'Grosir kaos kaki Gelea di Dropify Surabaya dengan MOQ umumnya 50 lusin. Cek motif, ukuran, stok, dan quotation melalui WhatsApp.',
    intro:
      'Kaos kaki ankle Gelea tersedia dalam pilihan polos, bunga, dan motif lain untuk stok toko. Minta pilihan motif, ukuran, isi kemasan, dan stok kepada admin.',
    detail:
      'Kaos kaki Gelea di katalog Dropify adalah seri ankle pendek dengan beberapa pilihan motif. MOQ kategori kaos kaki umumnya 50 lusin dan satu lusin berisi 12 pasang. Ketentuan campur motif perlu dikonfirmasi. Sampel dihitung dari harga satuan dan ongkir.',
    faq: [
      {
        q: 'Di mana bisa beli kaos kaki Gelea secara grosir?',
        a: 'Kaos kaki Gelea dapat dipesan melalui WhatsApp Dropify di +62 857-0401-1028. Pesanan dikirim dari Surabaya ke seluruh Indonesia. Kirim jumlah lusin dan kota tujuan agar admin dapat mengirim pilihan motif yang sedang tersedia.',
      },
      {
        q: 'Berapa harga dan minimum order kaos kaki Gelea di Dropify?',
        a: 'MOQ kaos kaki Gelea umumnya 50 lusin dengan isi 12 pasang per lusin. Ketentuan campur motif dan harga dikonfirmasi admin saat quotation dibuat.',
      },
      {
        q: 'Motif kaos kaki Gelea apa saja yang tersedia?',
        a: 'Pilihannya mencakup motif polos, bunga, hati, warna pastel, dan desain bergaya aesthetic pada model ankle. Motif berganti mengikuti produksi, jadi minta foto stok terbaru jika Anda perlu menyusun tema tertentu untuk toko.',
      },
      {
        q: 'Bagaimana cara order Gelea di Dropify?',
        a: 'Kirim motif atau tema yang dicari, jumlah lusin, dan kota tujuan melalui WhatsApp di +62 857-0401-1028. Admin akan mengirim foto stok, harga, ongkir, dan langkah pembayaran.',
      },
      {
        q: 'Bisakah pesan sampel Gelea dulu?',
        a: 'Bisa. Sampel dihitung dengan harga satuan ditambah ongkir. Anda dapat memeriksa bahan, elastisitas, ukuran, dan hasil motif sebelum membeli dalam jumlah grosir.',
      },
    ],
  },
  {
    slug: 'tuyaya',
    name: 'Tuyaya',
    searchTerm: 'tuyaya',
    categorySlugs: ['manshet-sarung-tangan', 'kaos-kaki'],
    lpByCategory: {
      'manshet-sarung-tangan': '/sarung-tangan-tuyaya',
      'kaos-kaki': '/kaos-kaki-tuyaya',
    },
    seoTitle: 'Grosir sarung tangan & kaos kaki Tuyaya',
    metaDescription:
      'Grosir produk Tuyaya di Dropify Surabaya: sarung tangan touch screen, gloves wool, kaos kaki pramuka, dan kaos kaki bayi. Cek stok via WhatsApp.',
    intro:
      'Tuyaya menawarkan sarung tangan dan kaos kaki untuk kebutuhan yang berbeda. Pilihannya mencakup sarung tangan touch screen, sarung tangan wool, kaos kaki pramuka, serta kaos kaki bayi seri BB311. Kirim nama produk serta jumlah yang dibutuhkan agar admin dapat mengecek pilihan dan stoknya.',
    detail:
      'Katalog Tuyaya di Dropify mencakup sarung tangan touch screen, gloves wool pria dan wanita, kaos kaki pramuka, serta kaos kaki bayi BB311T, BB311LNS, BB311LA, BB311TNS, dan BB311TAS. Satu lusin berisi 12 pcs. Kirim nomor model, jumlah lusin, serta kota tujuan agar admin dapat memeriksa stok dan ketentuan pesanan.',
    faq: [
      {
        q: 'Produk Tuyaya apa saja yang ada di Dropify?',
        a: 'Ada sarung tangan touch screen, sarung tangan wool pria dan wanita, kaos kaki pramuka, serta kaos kaki bayi seri BB311. Pilih jenis yang Anda perlukan, lalu admin akan mengirim pilihan model dan warna yang tersedia saat itu.',
      },
      {
        q: 'Berapa minimum order produk Tuyaya di Dropify?',
        a: 'Minimum order mengikuti kategori sarung tangan atau kaos kaki. Satu lusin berisi 12 pcs. Kirim jenis produk dan jumlah yang Anda pesan agar admin dapat menjelaskan ketentuan serta harga yang berlaku.',
      },
      {
        q: 'Bagaimana cara order Tuyaya di Dropify?',
        a: 'Kirim jenis produk Tuyaya, pilihan varian, jumlah lusin, dan kota tujuan melalui WhatsApp di +62 857-0401-1028. Admin akan mengirim foto stok, harga, ongkir, dan langkah pembayaran.',
      },
      {
        q: 'Bisakah campur ukuran, warna, atau motif saat order Tuyaya?',
        a: 'Bisa untuk sebagian besar produk, tetapi susunannya mengikuti jenis barang dan stok yang tersedia. Kirim pembagian ukuran, warna, atau motif yang Anda inginkan agar admin dapat mengecek kombinasi yang bisa disiapkan.',
      },
      {
        q: 'Di mana bisa beli produk Tuyaya secara grosir?',
        a: 'Sarung tangan dan kaos kaki Tuyaya dapat dipesan melalui WhatsApp Dropify di +62 857-0401-1028. Barang diproses dari Surabaya dan dapat dikirim ke seluruh Indonesia. Sebutkan jenis produk, jumlah lusin, dan kota tujuan agar admin dapat memeriksa stok.',
      },
    ],
  },
  {
    slug: 'beautiful',
    name: 'Beautiful',
    searchTerm: 'beautiful',
    categorySlugs: ['kaos-kaki'],
    seoTitle: 'Grosir kaos kaki jempol Beautiful',
    metaDescription:
      'Grosir kaos kaki dan stocking jempol Beautiful di Dropify Surabaya. Pilih model AS2J atau B2J, cek ukuran dan stok via WhatsApp.',
    intro:
      'Beautiful tersedia untuk toko yang mencari kaos kaki muslim jempol dan stocking jempol. Model AS2J serta B2J dapat dicocokkan dengan nomor model saat Anda melakukan restock.',
    detail:
      'Stocking Jempol Beautiful B2J memiliki pilihan ukuran S, M, dan L sesuai label produk. Kirim nama model, jumlah lusin, ukuran yang diperlukan, dan kota tujuan agar admin dapat memeriksa stok serta menyusun quotation.',
    faq: [
      {
        q: 'Model Beautiful apa yang tersedia di Dropify?',
        a: 'Tersedia Kaos Kaki Muslim Jempol Beautiful AS2J dan Stocking Jempol Beautiful B2J. Sebutkan nomor model saat menghubungi admin agar pengecekan stok lebih tepat.',
      },
      {
        q: 'Ukuran Stocking Jempol Beautiful B2J tersedia apa saja?',
        a: 'Stocking Jempol Beautiful B2J memiliki label ukuran S, M, dan L. Kirim pembagian ukuran yang Anda perlukan agar admin dapat memeriksa pilihan yang tersedia.',
      },
      {
        q: 'Berapa minimum order produk Beautiful?',
        a: 'Minimum order mengikuti ketentuan kategori kaos kaki. Kirim nomor model dan jumlah lusin agar admin Dropify dapat menjelaskan ketentuan serta harga yang berlaku.',
      },
      {
        q: 'Bisakah melihat pilihan Beautiful sebelum order?',
        a: 'Bisa. Kirim nomor model AS2J atau B2J, jumlah kebutuhan, dan kota tujuan. Admin akan mengirim informasi produk yang tersedia untuk membantu Anda memilih.',
      },
      {
        q: 'Data apa yang perlu dikirim saat meminta quotation Beautiful?',
        a: 'Kirim nama atau nomor model Beautiful, jumlah lusin, ukuran bila diperlukan, dan kota tujuan melalui WhatsApp. Admin akan mengecek stok, harga grosir, serta ongkir.',
      },
    ],
  },
  {
    slug: 'allawi-hijab',
    name: 'Allawi Hijab',
    searchTerm: 'allawi hijab',
    categorySlugs: ['manshet-sarung-tangan'],
    seoTitle: 'Grosir manset Allawi Hijab MM688',
    metaDescription:
      'Grosir Manset Allawi Hijab MM688 di Dropify Surabaya. Cek pilihan warna, stok, dan quotation untuk kebutuhan toko via WhatsApp.',
    intro:
      'Manset Allawi Hijab MM688 tersedia untuk melengkapi stok aksesori muslim. Gunakan nomor model MM688 saat meminta pilihan warna dan jumlah yang dapat dipenuhi.',
    detail:
      'Kebutuhan manset biasanya ditentukan oleh warna, jumlah lusin, dan kota pengiriman. Kirim ketiga data tersebut bersama nomor MM688 agar admin dapat memeriksa stok serta ketentuan pembelian grosir.',
    faq: [
      {
        q: 'Manset Allawi Hijab apa yang tersedia di Dropify?',
        a: 'Saat ini tersedia Manset Allawi Hijab MM688. Kirim nomor model MM688 dan warna yang dicari agar admin dapat memeriksa stok.',
      },
      {
        q: 'Apakah warna manset Allawi Hijab bisa dicek sebelum order?',
        a: 'Bisa. Kirim nomor model MM688 serta kebutuhan jumlah Anda. Admin akan mengecek pilihan warna yang tersedia sebelum pesanan diproses.',
      },
      {
        q: 'Berapa minimum order Manset Allawi Hijab?',
        a: 'Minimum order mengikuti ketentuan kategori manset dan sarung tangan. Kirim jumlah lusin serta kota tujuan agar admin dapat menjelaskan ketentuan pembelian.',
      },
      {
        q: 'Bisakah memesan Manset Allawi Hijab untuk stok toko?',
        a: 'Bisa. Kirim nomor model MM688, jumlah lusin, pilihan warna, dan kota tujuan melalui WhatsApp untuk mendapatkan pengecekan stok serta quotation.',
      },
      {
        q: 'Apa yang perlu dikirim untuk meminta quotation Allawi Hijab?',
        a: 'Kirim nomor model MM688, jumlah lusin, warna bila sudah ada pilihan, dan kota tujuan. Admin Dropify akan memeriksa stok, harga grosir, dan ongkir.',
      },
    ],
  },
  {
    slug: 'head-buff',
    name: 'Head Buff',
    searchTerm: 'head buff',
    categorySlugs: ['manshet-sarung-tangan'],
    seoTitle: 'Grosir Head Buff motif & polos',
    metaDescription:
      'Grosir Head Buff HG138 di Dropify Surabaya. Pilih motif tengkorak, loreng, campur, atau hitam polos. Cek stok via WhatsApp.',
    intro:
      'Head Buff HG138 tersedia dalam pilihan tengkorak, loreng, campur, dan hitam polos. Nomor atau nama varian memudahkan toko mencocokkan model saat meminta stok.',
    detail:
      'Pilih HG138-1 untuk motif tengkorak, HG138-3 untuk loreng, HG138-W untuk motif campur, atau HG138 hitam polos. Kirim varian, jumlah lusin, dan kota tujuan agar admin dapat memeriksa pilihan yang tersedia.',
    faq: [
      {
        q: 'Varian Head Buff apa yang tersedia di Dropify?',
        a: 'Tersedia Head Buff HG138-1 Tengkorak, HG138-3 Loreng, HG138-W Campur, serta HG138 Hitam Polos. Sebutkan nama varian saat meminta stok.',
      },
      {
        q: 'Bisakah mencampur motif Head Buff dalam satu pesanan?',
        a: 'Kirim pembagian motif dan jumlah lusin. Admin akan mengecek stok setiap varian HG138, lalu mengonfirmasi susunan pesanan yang dapat dipenuhi.',
      },
      {
        q: 'Berapa minimum order Head Buff?',
        a: 'Minimum order mengikuti ketentuan kategori manset dan sarung tangan. Kirim jumlah kebutuhan serta kota tujuan agar admin dapat menjelaskan harga dan ketentuannya.',
      },
      {
        q: 'Bagaimana cara memilih Head Buff untuk stok toko?',
        a: 'Mulai dari motif yang ingin Anda isi, lalu kirim daftar HG138-1, HG138-3, HG138-W, atau hitam polos beserta jumlahnya. Admin akan mengecek stok per varian.',
      },
      {
        q: 'Apa yang perlu dikirim saat order Head Buff?',
        a: 'Kirim nama varian Head Buff, jumlah lusin, pembagian motif bila diperlukan, dan kota tujuan melalui WhatsApp. Admin akan mengecek stok, harga grosir, serta ongkir.',
      },
    ],
  },
  {
    slug: 'sanrio',
    name: 'Sanrio',
    searchTerm: 'sanrio',
    categorySlugs: ['kaos-kaki'],
    seoTitle: 'Grosir kaos kaki anak Sanrio',
    metaDescription:
      'Grosir kaos kaki anak Sanrio Boy & Girl di Dropify Surabaya. Tanyakan pilihan motif, jumlah lusin, dan stok melalui WhatsApp.',
    intro:
      'Kaos Kaki Anak Sanrio Boy & Girl tersedia untuk toko perlengkapan anak yang membutuhkan pilihan motif untuk anak laki-laki dan perempuan.',
    detail:
      'Kirim jumlah lusin, target pembeli boy atau girl, serta kota tujuan. Admin akan mengecek motif yang tersedia dan susunan pesanan yang dapat dipenuhi.',
    faq: [
      {
        q: 'Produk Sanrio apa yang tersedia di Dropify?',
        a: 'Tersedia Kaos Kaki Anak Sanrio Boy & Girl. Kirim jumlah lusin dan kebutuhan motif boy atau girl agar admin dapat memeriksa stoknya.',
      },
      {
        q: 'Apakah pilihan motif kaos kaki Sanrio bisa dicek?',
        a: 'Bisa. Hubungi admin dengan jumlah yang Anda butuhkan. Admin akan mengirim pilihan motif yang tersedia dan dapat dipesan.',
      },
      {
        q: 'Berapa minimum order kaos kaki anak Sanrio?',
        a: 'Minimum order mengikuti ketentuan kategori kaos kaki. Kirim jumlah lusin dan kota tujuan agar admin Dropify dapat menjelaskan ketentuan serta harga yang berlaku.',
      },
      {
        q: 'Bisakah memilih kaos kaki Sanrio untuk stok anak laki-laki atau perempuan?',
        a: 'Bisa. Sebutkan apakah stok Anda ditujukan untuk pilihan boy, girl, atau keduanya. Admin akan memeriksa pilihan motif yang tersedia saat pemesanan.',
      },
      {
        q: 'Bagaimana cara meminta quotation Sanrio?',
        a: 'Kirim nama produk Kaos Kaki Anak Sanrio Boy & Girl, jumlah lusin, kebutuhan motif, dan kota tujuan via WhatsApp. Admin akan mengecek stok, harga grosir, dan ongkir.',
      },
    ],
  },
  {
    slug: 'happy-socks',
    name: 'Happy Socks',
    searchTerm: 'happy socks',
    categorySlugs: ['kaos-kaki'],
    seoTitle: 'Grosir kaos kaki bayi Happy Socks',
    metaDescription:
      'Grosir kaos kaki bayi Happy Socks YH04 di Dropify Surabaya. Model anti slip untuk usia 0 sampai 6 bulan sesuai label produk. Cek stok via WhatsApp.',
    intro:
      'Happy Socks tersedia dalam model kaos kaki bayi anti slip YH04. Label produknya menunjukkan rentang usia 0 sampai 6 bulan.',
    detail:
      'Untuk menyusun stok perlengkapan bayi, kirim nomor model YH04, jumlah lusin, dan kota tujuan. Admin dapat memeriksa stok serta ketentuan pembelian grosir yang berlaku.',
    faq: [
      {
        q: 'Model Happy Socks apa yang tersedia di Dropify?',
        a: 'Tersedia Kaos Kaki Bayi Happy Socks YH04. Nomor model ini membantu admin mencocokkan produk saat Anda meminta stok.',
      },
      {
        q: 'Happy Socks YH04 untuk usia berapa?',
        a: 'Label produk Happy Socks YH04 menunjukkan usia 0 sampai 6 bulan. Konfirmasikan kembali ukuran yang tersedia saat Anda menghubungi admin.',
      },
      {
        q: 'Apakah Happy Socks YH04 memiliki fitur anti slip?',
        a: 'Nama produk YH04 mencantumkan anti slip. Jika fitur ini menjadi syarat pembelian, minta admin mengirim detail produk yang tersedia sebelum order.',
      },
      {
        q: 'Berapa minimum order Happy Socks?',
        a: 'Minimum order mengikuti ketentuan kategori kaos kaki. Kirim jumlah lusin serta kota tujuan agar admin dapat menyiapkan quotation sesuai kebutuhan Anda.',
      },
      {
        q: 'Bagaimana cara order Happy Socks YH04?',
        a: 'Kirim nomor model YH04, jumlah lusin, dan kota tujuan melalui WhatsApp. Admin Dropify akan mengecek stok, harga grosir, serta ongkir sebelum pesanan diproses.',
      },
    ],
  },
  {
    slug: 'marvel',
    name: 'Marvel',
    searchTerm: 'marvel',
    categorySlugs: ['kaos-kaki'],
    seoTitle: 'Grosir kaos kaki bayi Marvel',
    metaDescription:
      'Grosir kaos kaki bayi motif Marvel SM1351, SM6911, dan SM3916 di Dropify Surabaya. Cek model, stok, dan quotation via WhatsApp.',
    intro:
      'Kaos kaki bayi motif Marvel tersedia dalam nomor model SM1351, SM6911, dan SM3916. Nomor model ini membantu toko membedakan pilihan saat menyusun stok.',
    detail:
      'Kirim daftar nomor model Marvel, jumlah lusin per model, dan kota tujuan. Admin akan memeriksa stok setiap model serta membantu menyiapkan quotation sebelum pesanan diproses.',
    faq: [
      {
        q: 'Model kaos kaki bayi Marvel apa saja yang tersedia?',
        a: 'Tersedia Kaos Kaki Bayi Marvel SM1351, SM6911, dan SM3916. Sebutkan nomor model saat bertanya agar admin dapat mengecek stok per pilihan.',
      },
      {
        q: 'Bisakah mencampur nomor model Marvel dalam satu pesanan?',
        a: 'Kirim pembagian jumlah untuk SM1351, SM6911, dan SM3916. Admin akan mengecek stok setiap model, lalu mengonfirmasi susunan yang dapat dipenuhi.',
      },
      {
        q: 'Berapa minimum order kaos kaki Marvel?',
        a: 'Minimum order mengikuti ketentuan kategori kaos kaki. Kirim total jumlah lusin dan kota tujuan agar admin dapat menjelaskan harga serta ketentuan pesanan.',
      },
      {
        q: 'Bagaimana cara memilih model Marvel untuk stok toko?',
        a: 'Gunakan nomor SM1351, SM6911, atau SM3916 sebagai referensi. Admin dapat mengecek ketersediaan tiap model sebelum Anda menetapkan komposisi pesanan.',
      },
      {
        q: 'Apa yang perlu dikirim untuk meminta quotation Marvel?',
        a: 'Kirim nomor model Marvel, jumlah lusin per model, dan kota tujuan via WhatsApp. Admin Dropify akan memeriksa stok, harga grosir, serta ongkir.',
      },
    ],
  },
  {
    slug: 'gekko',
    name: 'Gekko Grip',
    searchTerm: 'gekko',
    categorySlugs: ['manshet-sarung-tangan'],
    lpHref: '/sarung-tangan-gekko',
    seoTitle: 'Produk Gekko Grip di Dropify',
    metaDescription:
      'Lihat produk Gekko Grip di Dropify, lalu buka halaman kategori untuk membandingkan seri Nitrile dan PU, MOQ, stok, dan cara meminta quotation.',
    intro:
      'Gekko tersedia dalam lini Nitrile dan PU untuk kebutuhan bengkel, pabrik, proyek, gudang, dan toko safety. Kirim kondisi kerja, ukuran, serta jumlah yang dibutuhkan agar admin dapat membantu memilih seri.',
    detail:
      'Sarung tangan Gekko di Dropify tersedia dalam pilihan lapisan Nitrile dan PU. Minimum order 50 lusin, 1 lusin isi 12 pasang, dengan harga khusus untuk partai besar. Lihat pilihan produknya di katalog, lalu kirim nama seri, jumlah, dan kota tujuan melalui WhatsApp.',
    faq: [
      {
        q: 'Sarung tangan Gekko Grip cocok untuk pekerjaan apa?',
        a: 'Seri Nitrile biasa dipilih untuk handling komponen, pekerjaan bengkel, maintenance, dan area yang berpotensi berminyak. Seri PU lebih sesuai untuk perakitan, pengepakan, serta penanganan barang kering yang membutuhkan ketangkasan tangan. Kirim kondisi pemakaian Anda agar admin dapat membantu memilih seri.',
      },
      {
        q: 'Berapa minimum order sarung tangan Gekko Grip di Dropify?',
        a: 'Minimum ordernya 50 lusin, dengan isi 12 pasang per lusin. Untuk kebutuhan partai besar, kirim jumlah dan jadwal pemakaian agar admin dapat menyiapkan harga volume serta rencana pengiriman.',
      },
      {
        q: 'Bagaimana cara order Gekko Grip di Dropify?',
        a: 'Kirim nama seri atau foto, jenis pekerjaan, perkiraan jumlah, pembagian ukuran, dan kota tujuan melalui WhatsApp di +62 857-0401-1028. Admin akan mengecek stok, harga volume, ongkir, dan dokumen produk yang tersedia.',
      },
      {
        q: 'Berapa lama pengiriman Gekko Grip dari Dropify?',
        a: 'Pesanan Gekko Grip dikirim dari Surabaya. Admin mengonfirmasi waktu penyiapan dan memberi estimasi perjalanan berdasarkan kota tujuan serta layanan ekspedisi.',
      },
      {
        q: 'Di mana bisa beli sarung tangan Gekko Grip secara grosir?',
        a: 'Sarung tangan Gekko Grip dapat dipesan melalui WhatsApp Dropify di +62 857-0401-1028. Pesanan diproses dari Surabaya dan dapat dikirim ke seluruh Indonesia. Kirim seri yang dicari, jumlah lusin, dan kota tujuan agar admin dapat mengecek stok serta ongkir.',
      },
    ],
  },
  {
    slug: 'matahari',
    name: 'Matahari',
    searchTerm: 'matahari',
    categorySlugs: ['manshet-sarung-tangan'],
    lpHref: '/sarung-tangan-matahari',
    seoTitle: 'Produk grosir Matahari',
    metaDescription:
      'Lihat pilihan produk Matahari di Dropify dan buka halaman kategori khusus untuk membandingkan varian, MOQ, sample, stok, dan cara meminta quotation.',
    intro:
      'Matahari tersedia di Dropify pada kategori sarung tangan kerja rajut berbintik. Buka halaman kategori khusus untuk membandingkan empat warna, MOQ, sample, dan cara meminta quotation.',
    detail:
      'Pilihan Matahari saat ini terdiri dari empat warna all size dalam kemasan 12 pasang per lusin. Rincian penggunaan, batas pemakaian, susunan warna, dan proses order tersedia pada halaman grosir sarung tangan Matahari.',
    faq: [
      {
        q: 'Sarung tangan Matahari tersedia varian apa saja?',
        a: 'Ada empat pilihan warna: putih dengan bintik hitam, navy dengan bintik, merah bendera dengan bintik hitam, dan biru muda dengan bintik hitam. Semuanya berukuran all size dan dikemas 12 pasang per lusin. Jika Anda ingin mencampur warna, kirim pembagian jumlahnya agar admin dapat mengecek stok masing-masing.',
      },
      {
        q: 'Berapa harga dan minimum order sarung tangan Matahari di Dropify?',
        a: 'Minimum ordernya mulai 25 lusin atau 300 pasang, dengan isi 12 pasang per lusin. Harga mengikuti warna, jumlah pesanan, dan stok yang tersedia. Kirim kebutuhan Anda agar admin dapat memberikan harga yang sesuai.',
      },
      {
        q: 'Bagaimana cara order Matahari di Dropify?',
        a: 'Kirim pilihan warna, jumlah lusin, pembagian per warna, dan kota tujuan melalui WhatsApp di +62 857-0401-1028. Admin akan mengecek stok, mengirim foto barang aktual, lalu memberikan harga, ongkir, dan langkah pembayaran.',
      },
      {
        q: 'Bisakah pesan sampel Matahari dulu?',
        a: 'Bisa. Sampel dihitung dengan harga satuan ditambah ongkir. Anda dapat mengecek rajutan, ukuran, warna, dan bintik pada telapak sebelum membeli untuk kebutuhan toko atau tim kerja.',
      },
      {
        q: 'Di mana bisa beli sarung tangan Matahari secara grosir?',
        a: 'Sarung tangan Matahari dapat dipesan melalui WhatsApp Dropify di +62 857-0401-1028. Pesanan diproses dari Surabaya dan dapat dikirim ke seluruh Indonesia. Sebutkan warna, jumlah lusin, dan kota tujuan agar admin dapat memeriksa stok serta ongkir.',
      },
    ],
  },
  {
    slug: 'brilles',
    name: 'Brilles',
    searchTerm: 'brilles',
    categorySlugs: ['manshet-sarung-tangan'],
    lpByCategory: { 'manshet-sarung-tangan': '/manset-brilles' },
    seoTitle: 'Grosir manset tangan Brilles',
    metaDescription:
      'Grosir manset tangan Brilles di Dropify Surabaya: polos, kerut, renda, dan jempol. Cek model, warna, stok, serta quotation melalui WhatsApp.',
    intro:
      'Manset tangan Brilles tersedia dalam model polos, kerut, renda, dan jempol. Minta pilihan warna, ukuran, isi kemasan, dan model yang tersedia kepada admin.',
    detail:
      'Katalog manset Brilles di Dropify mencakup seri M212 sampai M218: manset tangan polos warna, kerut, kerut solid, renda, dan manset jempol. MOQ 50 lusin, 1 lusin isi 12 pcs, dan sebagian besar produk bisa campur warna dalam satu order. Sampel bisa dipesan dengan harga satuan plus ongkir. Konfirmasi stok via WhatsApp sebelum order.',
    faq: [
      {
        q: 'Model manset Brilles apa saja yang tersedia di Dropify?',
        a: 'Pilihannya meliputi manset polos warna, kerut, kerut solid, renda, dan manset jempol pada seri M212 sampai M218. Minta foto stok terbaru agar Anda dapat membandingkan model dan warna sebelum menentukan jumlah.',
      },
      {
        q: 'Berapa harga dan minimum order manset Brilles di Dropify?',
        a: 'MOQ manset Brilles umumnya 50 lusin dengan isi 12 pcs per lusin. Ketentuan campur warna dan harga dikonfirmasi admin saat quotation dibuat.',
      },
      {
        q: 'Bagaimana cara order Brilles di Dropify?',
        a: 'Kirim nama model atau foto, warna yang diminati, jumlah lusin, dan kota tujuan melalui WhatsApp di +62 857-0401-1028. Admin akan mengirim informasi stok, harga, ongkir, dan langkah pembayaran.',
      },
      {
        q: 'Bisakah pesan sampel Brilles dulu?',
        a: 'Bisa. Sampel dihitung dengan harga satuan ditambah ongkir. Anda dapat mengecek bahan, panjang manset, detail jahitan, dan warna sebelum membeli dalam jumlah grosir.',
      },
      {
        q: 'Di mana bisa beli manset Brilles secara grosir?',
        a: 'Manset Brilles dapat dipesan melalui WhatsApp Dropify di +62 857-0401-1028. Barang dikirim dari Surabaya ke seluruh Indonesia. Kirim model, warna, jumlah lusin, dan kota tujuan agar admin dapat mengecek stok yang tersedia.',
      },
    ],
  },
  {
    slug: 'winteku',
    name: 'Winteku',
    searchTerm: 'winteku',
    categorySlugs: ['kaos-kaki'],
    lpByCategory: { 'kaos-kaki': '/kaos-kaki-winteku' },
    seoTitle: 'Grosir kaos kaki anak Winteku',
    metaDescription:
      'Grosir kaos kaki bayi dan anak Winteku di Dropify Surabaya. Cek motif, ukuran, stok, dan quotation melalui WhatsApp.',
    intro:
      'Winteku menyediakan kaos kaki bayi dan anak dalam pilihan motif boy serta girl. Sebutkan ukuran atau rentang usia target agar admin dapat menunjukkan pilihan yang relevan.',
    detail:
      'Kaos kaki Winteku tersedia dalam varian bayi dan anak. Satu lusin berisi 12 pasang. Konfirmasikan MOQ, pilihan motif, ketentuan campur ukuran atau warna, serta estimasi pengiriman melalui WhatsApp.',
    faq: [
      {
        q: 'Kaos kaki Winteku tersedia untuk usia apa saja?',
        a: 'Winteku tersedia untuk bayi dan anak, dengan pilihan motif boy dan girl. Ukuran serta motif mengikuti produksi dan stok yang sedang ada. Sebutkan rentang usia target agar admin dapat mengirim pilihan yang lebih relevan.',
      },
      {
        q: 'Berapa harga grosir kaos kaki Winteku di Dropify?',
        a: 'Satu lusin kaos kaki Winteku berisi 12 pasang. Harga mengikuti model dan jumlah. Minta foto stok untuk memilih motif sebelum admin menyiapkan quotation.',
      },
      {
        q: 'Berapa minimum order kaos kaki Winteku di Dropify?',
        a: 'Minimum ordernya 50 lusin, dengan isi 12 pcs per lusin. Sebagian besar pesanan dapat disusun dengan campuran ukuran dan warna. Kirim pembagian yang Anda perlukan agar admin dapat memeriksa kombinasi yang tersedia.',
      },
      {
        q: 'Di mana bisa beli kaos kaki Winteku secara grosir?',
        a: 'Kaos kaki bayi dan anak Winteku dapat dipesan melalui WhatsApp Dropify di +62 857-0401-1028. Pesanan dikirim dari Surabaya ke seluruh Indonesia. Kirim kelompok usia, jumlah lusin, dan kota tujuan agar admin dapat mengirim pilihan motif yang tersedia.',
      },
      {
        q: 'Berapa lama pengiriman kaos kaki Winteku dari Dropify?',
        a: 'Pesanan Winteku dikirim dari Surabaya. Admin mengonfirmasi waktu penyiapan dan memberi estimasi perjalanan berdasarkan kota tujuan serta layanan ekspedisi.',
      },
    ],
  },
  {
    slug: 'lingcao',
    name: 'Lingcao',
    searchTerm: 'lingcao',
    categorySlugs: ['bra-lingerie'],
    seoTitle: 'Grosir bra Lingcao',
    metaDescription:
      'Grosir bra Lingcao di Dropify Surabaya. Model mencakup BH A5 dan BH E66. Cek ukuran, warna, stok, dan quotation melalui WhatsApp.',
    intro:
      'Bra Lingcao tersedia dalam model seperti BH A5 dan BH E66. Kirim model, ukuran, warna, jumlah, dan kota tujuan untuk meminta quotation.',
    detail:
      'MOQ kategori bra 25 lusin dan satu lusin berisi 12 pcs. Ketentuan campur ukuran atau warna perlu diperiksa untuk model yang dipilih. Sampel dihitung dengan harga satuan ditambah ongkir. Pemesanan dilakukan melalui WhatsApp.',
    faq: [
      {
        q: 'Bagaimana memeriksa stok bra Lingcao?',
        a: 'Kirim nama model, ukuran, warna, dan jumlah. Admin akan memeriksa ketersediaannya serta mengirim foto produk sebelum pembayaran.',
      },
      {
        q: 'Model bra Lingcao apa saja yang sering masuk Dropify?',
        a: 'Model yang tercantum di katalog antara lain BH A5 dan BH E66. Konfirmasikan model, ukuran, warna, dan jumlah yang tersedia melalui WhatsApp sebelum order.',
      },
      {
        q: 'Berapa harga grosir bra Lingcao?',
        a: 'Satu lusin bra Lingcao berisi 12 pcs. Harga mengikuti model dan jumlah serta dikonfirmasi bersama stok saat quotation dibuat.',
      },
      {
        q: 'Berapa MOQ bra Lingcao dan apakah bisa campur ukuran?',
        a: 'MOQ kategori bra 25 lusin. Kirim komposisi ukuran dan warna yang dibutuhkan agar admin dapat memeriksa ketentuan campur untuk model tersebut.',
      },
      {
        q: 'Apakah bisa pesan sampel bra Lingcao?',
        a: 'Bisa. Sampel dihitung harga satuan plus ongkir. Periksa bahan, bentuk cup, dan ukuran sebelum memesan 25 lusin atau lebih.',
      },
    ],
  },
  {
    slug: 'fengyuer',
    name: 'Fengyuer',
    searchTerm: 'fengyuer',
    categorySlugs: ['bra-lingerie'],
    lpHref: '/bra-fengyuer',
    seoTitle: 'Grosir bra Fengyuer',
    metaDescription:
      'Grosir bra Fengyuer BH 128 Super Soft Wireless di Dropify Surabaya dengan MOQ umumnya 25 lusin. Cek stok dan quotation melalui WhatsApp.',
    intro:
      'Fengyuer masuk katalog Dropify dengan model BH 128 Super Soft Wireless untuk pemakaian harian. Kirim foto atau nama model, ukuran, warna, dan jumlah saat bertanya.',
    detail:
      'Katalog Dropify memuat bra Fengyuer BH 128 Super Soft Wireless. MOQ bra umumnya 25 lusin dan satu lusin berisi 12 pcs. Ketentuan campur ukuran serta warna mengikuti stok model. Sampel dihitung dari harga satuan dan ongkir.',
    faq: [
      {
        q: 'Model bra Fengyuer apa yang tersedia di Dropify?',
        a: 'Saat ini tersedia model BH 128 Fengyuer Super Soft Wireless untuk pemakaian harian. Stok mengikuti produksi, jadi kirim foto atau nama model saat bertanya.',
      },
      {
        q: 'Berapa harga dan minimum order bra Fengyuer?',
        a: 'MOQ bra Fengyuer umumnya 25 lusin dengan isi 12 pcs per lusin. Harga mengikuti model dan jumlah serta dikonfirmasi melalui quotation.',
      },
      {
        q: 'Bisakah campur ukuran dan warna saat order Fengyuer?',
        a: 'Sebagian besar model bisa campur ukuran dan warna dalam satu lusin selama stok tersedia. Kirim pembagian yang Anda inginkan agar admin dapat memastikan komposisinya.',
      },
      {
        q: 'Apakah bisa pesan sampel bra Fengyuer?',
        a: 'Bisa. Sampel dihitung harga satuan plus ongkir. Gunakan untuk mengecek bahan, bentuk cup, jahitan, dan ukuran sebelum memesan 25 lusin atau lebih.',
      },
      {
        q: 'Bagaimana cara order Fengyuer untuk stok toko?',
        a: 'Kirim model atau foto bra Fengyuer, jumlah lusin, kebutuhan ukuran dan warna, serta kota tujuan via WhatsApp +62 857-0401-1028. Admin membalas dengan informasi stok, harga, ongkir, dan langkah pembayaran.',
      },
    ],
  },
  {
    slug: 'daililing',
    name: 'Daililing',
    searchTerm: 'daililing',
    categorySlugs: ['bra-lingerie'],
    lpHref: '/bra-daililing',
    seoTitle: 'Grosir bra Daililing',
    metaDescription:
      'Grosir bra Daililing BH 358 di Dropify Surabaya dengan MOQ umumnya 25 lusin. Cek stok dan quotation melalui WhatsApp.',
    intro:
      'Daililing tersedia di katalog Dropify dengan model BH 358 untuk pemakaian harian. Kirim foto atau nama model, ukuran, warna, dan jumlah agar admin dapat memeriksa stok serta menyiapkan quotation.',
    detail:
      'Katalog Dropify memuat bra Daililing dengan model BH 358. MOQ 25 lusin, satu lusin isi 12 pcs, dan campur ukuran serta warna bisa dilakukan selama stok tersedia. Sampel dihitung harga satuan plus ongkir. Konfirmasi stok via WhatsApp sebelum order.',
    faq: [
      {
        q: 'Model bra Daililing apa yang tersedia di Dropify?',
        a: 'Saat ini tersedia model BH 358 Daililing untuk pemakaian harian. Stok mengikuti produksi, jadi kirim foto atau nama model saat bertanya.',
      },
      {
        q: 'Berapa harga dan minimum order bra Daililing?',
        a: 'MOQ bra Daililing umumnya 25 lusin dengan isi 12 pcs per lusin. Harga mengikuti model dan jumlah serta dikonfirmasi melalui quotation.',
      },
      {
        q: 'Bisakah campur ukuran dan warna saat order Daililing?',
        a: 'Sebagian besar model bisa campur ukuran dan warna dalam satu lusin selama stok tersedia. Kirim pembagian yang Anda inginkan agar admin dapat memastikan komposisinya.',
      },
      {
        q: 'Apakah bisa pesan sampel bra Daililing?',
        a: 'Bisa. Sampel dihitung harga satuan plus ongkir. Gunakan untuk mengecek bahan, bentuk cup, jahitan, dan ukuran sebelum memesan 25 lusin atau lebih.',
      },
      {
        q: 'Bagaimana cara order Daililing untuk stok toko?',
        a: 'Kirim model atau foto bra Daililing, jumlah lusin, kebutuhan ukuran dan warna, serta kota tujuan via WhatsApp +62 857-0401-1028. Admin membalas dengan informasi stok, harga, ongkir, dan langkah pembayaran.',
      },
    ],
  },
]

export function getBrand(slug: string): Brand | undefined {
  return BRANDS.find((brand) => brand.slug === slug)
}

export function getBrandLandingHref(brand: Brand, categorySlug?: string) {
  if (categorySlug && brand.lpByCategory?.[categorySlug]) {
    return brand.lpByCategory[categorySlug]
  }

  // The canonical brand entry point is always the hub page.
  // Dedicated LPs are linked from the hub as category-specific destinations.
  return `/merek/${brand.slug}`
}
